import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ServiceDeskAPIService from "@sd/services/APIs";
import type { CustomerCompany } from "@sd/types/customerCompany";
import { extractListArray } from "@sd/lib/formilyGridTicketCache";
import {
  mapFormilyToCustomerCompany,
  mergeCompaniesFromGrid,
} from "@sd/lib/customerCompanyFromFormily";
import {
  SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
  serviceDeskFormilyCustomerCompanyGridQueryKey,
} from "@sd/services/APIs";

type CompaniesAdminContextValue = {
  companies: CustomerCompany[];
  setCompanies: React.Dispatch<React.SetStateAction<CustomerCompany[]>>;
  invalidateCompanyGrid: () => void;
  companiesLoading: boolean;
  companiesError: boolean;
};

const CompaniesAdminContext = createContext<CompaniesAdminContextValue | null>(
  null
);

export function CompaniesAdminProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [companies, setCompanies] = useState<CustomerCompany[]>([]);

  const {
    data: companyGridPayload,
    isPending: companiesLoading,
    isError: companiesError,
  } = useQuery({
    queryKey: serviceDeskFormilyCustomerCompanyGridQueryKey,
    queryFn: async () => {
      const data = await ServiceDeskAPIService.formily.getGridData(
        SERVICE_DESK_FORMILY_CUSTOMER_COMPANY_MODEL,
        1,
        200
      );
      if (data === undefined) {
        throw new Error("Failed to load companies");
      }
      return data;
    },
    staleTime: 30_000,
    retry: 1,
  });

  useEffect(() => {
    if (companyGridPayload === undefined) {
      return;
    }
    const rows = extractListArray(companyGridPayload).filter(
      (r): r is Record<string, unknown> =>
        r !== null && typeof r === "object" && !Array.isArray(r)
    );
    const mapped = rows.map(r =>
      mapFormilyToCustomerCompany(r as Record<string, unknown>)
    );
    setCompanies(prev => mergeCompaniesFromGrid(mapped, prev));
  }, [companyGridPayload]);

  const invalidateCompanyGrid = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: serviceDeskFormilyCustomerCompanyGridQueryKey,
    });
  }, [queryClient]);

  const value = useMemo(
    () => ({
      companies,
      setCompanies,
      invalidateCompanyGrid,
      companiesLoading,
      companiesError,
    }),
    [companies, invalidateCompanyGrid, companiesLoading, companiesError]
  );

  return (
    <CompaniesAdminContext.Provider value={value}>
      {children}
    </CompaniesAdminContext.Provider>
  );
}

export function useCompaniesAdmin() {
  const ctx = useContext(CompaniesAdminContext);
  if (!ctx) {
    throw new Error("useCompaniesAdmin must be used within CompaniesAdminProvider");
  }
  return ctx;
}
